import { Link } from "react-router-dom";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { SITELINKS } from "../../../app-routes/links";
import { DashboardSummary } from "../../../interface/dashboard.interface";
import ChartTooltip from "./ChartTooltip";
import DashCard from "./DashCard";
import { fmtCompact, fmtCount, pctOf } from "./format";

const STATUSES = [
  { key: "active", label: "Active", status: "ACTIVE", color: "#22C55E" },
  { key: "pending", label: "Pending", status: "PENDING", color: "#F3A838" },
  { key: "flagged", label: "Flagged", status: "FLAGGED", color: "#EF4444" },
  { key: "removed", label: "Removed", status: "REMOVED", color: "#94A3B8" },
] as const;

const GENDERS = [
  { key: "MALE", label: "Male", color: "#7600BC" },
  { key: "FEMALE", label: "Female", color: "#F3A838" },
  { key: "OTHERS", label: "Other", color: "#94A3B8" },
];

const UserStatusCard = ({ data, className }: { data: DashboardSummary; className?: string }) => {
  const users = data.users ?? {};
  const rows = STATUSES.map((s) => ({ ...s, value: users[s.key] }));
  const known = rows.filter((r) => r.value !== undefined);
  const sum = known.reduce((s, r) => s + (r.value ?? 0), 0);
  const total = users.total ?? sum;

  const genders = data.gender_split;
  const gTotal = genders ? GENDERS.reduce((s, g) => s + (Number(genders[g.key]) || 0), 0) : 0;

  return (
    <DashCard
      title="Users by status"
      empty={known.length === 0}
      className={className}
    >
      <div className="flex items-center gap-5">
        <div className="relative h-36 w-36 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={known}
                dataKey="value"
                nameKey="label"
                innerRadius="70%"
                outerRadius="100%"
                paddingAngle={known.length > 1 ? 2 : 0}
                stroke="none"
              >
                {known.map((r) => (
                  <Cell key={r.key} fill={r.color} />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-xl font-semibold text-honeConnectBlack">{fmtCompact(total)}</span>
            <span className="text-[11px] text-hcMuted">total</span>
          </div>
        </div>

        <ul className="min-w-0 flex-1 space-y-1">
          {rows.map((r) => {
            const pct = pctOf(r.value, total);
            return (
              <li key={r.key}>
                <Link
                  to={`${SITELINKS.ELITE_USER}?status=${r.status}`}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-hcBg"
                >
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: r.color }} />
                  <span className="text-honeConnectGray">{r.label}</span>
                  <span className="ml-auto font-semibold text-honeConnectBlack">{fmtCount(r.value)}</span>
                  <span className="w-11 text-right text-xs text-hcMuted">
                    {pct !== undefined ? `${pct.toFixed(0)}%` : ""}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="mt-5 border-t border-hcBorder pt-4">
        <p className="mb-2 text-xs font-medium text-hcMuted">By gender</p>
        {gTotal > 0 ? (
          <>
            <div className="flex h-2.5 overflow-hidden rounded-full bg-hcBg">
              {GENDERS.map((g) => (
                <span
                  key={g.key}
                  style={{ width: `${((Number(genders![g.key]) || 0) / gTotal) * 100}%`, background: g.color }}
                />
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-hcMuted">
              {GENDERS.map((g) => (
                <span key={g.key} className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full" style={{ background: g.color }} />
                  {g.label} {(((Number(genders![g.key]) || 0) / gTotal) * 100).toFixed(0)}%
                </span>
              ))}
            </div>
          </>
        ) : (
          <p className="text-xs text-gray-400">Waiting for gender_split from the API.</p>
        )}
      </div>
    </DashCard>
  );
};

export default UserStatusCard;
